const ONLOAD_CALLBACK = 'onloadTurnstile';
const SCRIPT_URL = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${ONLOAD_CALLBACK}`;

export const TURNSTILE_PENDING_MESSAGE =
  "We couldn't verify your browser yet. Please wait a moment and try again. If this keeps happening, disable any ad blocker or privacy extension and reload the page.";

let scriptPromise = null;

// The script is loaded once and shared by every form, so the global `onload`
// callback is owned by this loader and never by a component. Components that
// set it themselves overwrite each other, since it is a single slot.
function loadTurnstile() {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      window[ONLOAD_CALLBACK] = () => resolve(window.turnstile);
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        scriptPromise = null;
        script.remove();
        reject(new Error('Unable to load Turnstile'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Renders a Turnstile widget into `<div ref="turnstile" />` and keeps the
 * resulting token in `turnstileToken`.
 */
export default {
  data() {
    return {
      turnstileToken: '',
      turnstileWidgetId: null
    };
  },
  computed: {
    turnstileEnabled() {
      return !!process.env.CF_TURNSTILE_SITE_KEY;
    },
    turnstileReady() {
      return !this.turnstileEnabled || !!this.turnstileToken;
    }
  },
  mounted() {
    this.renderTurnstile();
  },
  beforeDestroy() {
    this.removeTurnstile();
  },
  methods: {
    async renderTurnstile() {
      if (!this.turnstileEnabled) return;

      let turnstile;
      try {
        turnstile = await loadTurnstile();
      } catch (err) {
        return;
      }

      // When navigating between layouts, Nuxt briefly mounts a copy of the
      // page inside the previous layout. That copy is never attached to the
      // document, so only the instance that is actually visible gets a widget.
      const container = this.$refs.turnstile;
      if (this._isDestroyed || !container || !container.isConnected) return;

      this.turnstileWidgetId = turnstile.render(container, {
        sitekey: process.env.CF_TURNSTILE_SITE_KEY,
        callback: (token) => {
          this.turnstileToken = token;
        },
        'expired-callback': () => {
          this.turnstileToken = '';
        },
        'error-callback': () => {
          this.turnstileToken = '';
        }
      });
    },
    // Tokens are single-use, so a new one is needed after every submission.
    resetTurnstile() {
      this.turnstileToken = '';
      if (this.turnstileWidgetId && window.turnstile) {
        window.turnstile.reset(this.turnstileWidgetId);
      }
    },
    removeTurnstile() {
      if (this.turnstileWidgetId && window.turnstile) {
        window.turnstile.remove(this.turnstileWidgetId);
      }
      this.turnstileWidgetId = null;
    }
  }
};
